import { useState, useCallback, useMemo, useEffect } from 'react';
import { deliveryKeys } from 'common/constants/settings';
import SettingsApi from 'libs/api/settings';
import { useCommon } from 'redux/common/use-common';
import moment from 'moment';
import { nanoid } from 'nanoid';
import { useSettings } from 'redux/settings/use-settings';
import { useDynamicApi } from '../api/dynamic-api';
import RestaurantsApi from '../../libs/api/restaurants';
import { fetchAllPages } from 'utils/pagination';
import UIHelper from '../../services/UIHelper';

export const useSettingsForm = () => {
  const [deliveryZips, setDeliveryZips] = useState([]);
  const { currentRestaurant } = useCommon();
  const [deliveryActiveTab, setDeliveryActiveTab] = useState(
    deliveryKeys.reservation,
  );
  const [loadingStatuses, setLoadingStatuses] = useState({
    [deliveryKeys.reservation]: true,
    [deliveryKeys.delivery]: false,
    [deliveryKeys.takeaway]: false,
  });

  const isLoading =
    loadingStatuses[deliveryKeys.reservation] ||
    loadingStatuses[deliveryKeys.delivery] ||
    loadingStatuses[deliveryKeys.takeaway];

  const api = useMemo(() => {
    return new SettingsApi(currentRestaurant?.id);
  }, [currentRestaurant]);

  const [deliveryDates, setDeliveryDates] = useState({
    [deliveryKeys.reservation]: {
      selectedDay: new Date(),
      opening_hours: [],
      opening_hour_exceptions: [],
      settings: {
        max_delivery_orders: 1,
      },
      isFetched: false,
      errors: null,
    },
    [deliveryKeys.delivery]: {
      selectedDay: new Date(),
      opening_hours: [],
      settings: {
        max_delivery_orders: 1,
      },
      opening_hour_exceptions: [],
      isFetched: false,
      errors: null,
    },
    [deliveryKeys.takeaway]: {
      opening_hours: [],
      selectedDay: new Date(),
      opening_hour_exceptions: [],
      settings: {
        max_delivery_orders: 1,
      },
      isFetched: false,
      errors: null,
    },
  });

  const onChangeMaximum = useCallback(
    (e) => {
      const {
        target: { name, value },
      } = e;
      setDeliveryDates((state) => {
        return {
          ...state,
          [deliveryActiveTab]: {
            ...state[deliveryActiveTab],
            settings: {
              ...(state[deliveryActiveTab]?.settings || {}),
              [name]: value,
            },
          },
        };
      });
    },
    [deliveryActiveTab],
  );

  const onChangeExceptionDay = (day, variant, selectedExceptionId) => {
    setDeliveryDates((state) => {
      const opening_hour_exceptions = [];
      state[deliveryActiveTab].opening_hour_exceptions.forEach((exc) => {
        if (exc.id === selectedExceptionId) {
          opening_hour_exceptions.push({
            ...exc,
            [variant]: day,
          });
          return;
        }
        opening_hour_exceptions.push(exc);
      });
      return {
        ...state,
        [deliveryActiveTab]: {
          ...state[deliveryActiveTab],
          selectedDay: day,
          opening_hour_exceptions,
        },
      };
    });
  };

  const onAddNewException = useCallback(
    async (newData) => {
      // If newData contains opening_hour_exceptions, it's a full data object from server
      if (newData.opening_hour_exceptions) {
        setDeliveryDates((state) => ({
          ...state,
          [deliveryActiveTab]: {
            ...state[deliveryActiveTab],
            ...newData,
            isFetched: true,
            errors: null,
          },
        }));
        return;
      }

      const {
        id,
        day_off_type,
        working_intervals,
        closing_intervals,
        from,
        to,
        break_from,
        break_to,
        ...rest
      } = newData;

      const exceptionPayload = {
        ...rest,
        date_from: moment(newData.date_from).format('YYYY-MM-DD'),
        date_to: moment(newData.date_to).format('YYYY-MM-DD'),
        day_off: !!newData.day_off,
      };

      if (Number.isInteger(id)) {
        exceptionPayload.id = id;
      }

      const workingIntervalsAttrs =
        newData.working_intervals_attributes || working_intervals;
      const closingIntervalsAttrs =
        newData.closing_intervals_attributes || closing_intervals;

      if (!exceptionPayload.day_off && workingIntervalsAttrs?.length) {
        exceptionPayload.working_intervals_attributes = workingIntervalsAttrs.map(
          ({ from: intervalFrom, to: intervalTo }) => ({
            from: intervalFrom,
            to: intervalTo,
          }),
        );
      }

      if (exceptionPayload.day_off && closingIntervalsAttrs?.length) {
        exceptionPayload.closing_intervals_attributes = closingIntervalsAttrs.map(
          ({ from: intervalFrom, to: intervalTo }) => ({
            from: intervalFrom,
            to: intervalTo,
          }),
        );
      }

      // Avoid sending nested association keys without _attributes
      delete exceptionPayload.working_intervals;
      delete exceptionPayload.closing_intervals;
      delete exceptionPayload.day_off_type;

      try {
        await api.put(`opening_hours/${deliveryActiveTab}`, {
          data: {
            opening_hour_exceptions_attributes: [exceptionPayload],
          },
        });

        const { data } = await api.get(`opening_hours/${deliveryActiveTab}`);
        setDeliveryDates((state) => ({
          ...state,
          [deliveryActiveTab]: {
            ...state[deliveryActiveTab],
            isFetched: true,
            errors: null,
            ...data,
          },
        }));
        UIHelper.showSuccessMessage();
      } catch (e) {
        console.log(e);
        UIHelper.showError(
          e?.response?.data?.message ||
            "Errore durante il salvataggio dell'eccezione",
        );
        throw e;
      }
    },
    [api, deliveryActiveTab],
  );

  const onAddExceptionData = (data) => {
    setDeliveryDates((state) => {
      let newExceptionsDates = [];
      state[deliveryActiveTab].opening_hour_exceptions.forEach((exc) => {
        if (exc.id === data.id) {
          newExceptionsDates.push({
            ...exc,
            ...data,
          });
          return;
        }
        newExceptionsDates.push(exc);
      });
      // if (state[deliveryActiveTab].opening_hour_exceptions?.length) {
      //   const hasThatDay = state[
      //     deliveryActiveTab
      //   ].opening_hour_exceptions.find((e) => {
      //     const current = moment(e.date).format('YYYY-MM-DD');
      //     const dataDate = moment(data.date).format('YYYY-MM-DD');
      //     return moment(current).isSame(dataDate);
      //   });
      //   if (!hasThatDay) {
      //     newExceptionsDates = [
      //       ...state[deliveryActiveTab].opening_hour_exceptions,
      //       data,
      //     ];
      //   } else {
      //     newExceptionsDates = [
      //       ...state[deliveryActiveTab].opening_hour_exceptions,
      //     ].map((e) => {
      //       const current = moment(e.date).format('YYYY-MM-DD');
      //       const dataDate = moment(data.date).format('YYYY-MM-DD');
      //       return moment(current).isSame(dataDate) ? data : e;
      //     });
      //   }
      // } else {
      //   newExceptionsDates.push(data);
      // }

      return {
        ...state,
        [deliveryActiveTab]: {
          ...state[deliveryActiveTab],
          opening_hour_exceptions: newExceptionsDates,
        },
      };
    });
  };

  const setHoursErrors = (errors) => {
    setDeliveryDates((state) => {
      return {
        ...state,
        [deliveryActiveTab]: {
          ...state[deliveryActiveTab],
          errors,
        },
      };
    });
  };

  const changeDeliveryDayConfig = useCallback(
    async (day, data = {}) => {
      const currentTab = deliveryDates[deliveryActiveTab];
      const newOpeningHours = [...(currentTab?.opening_hours || [])].map(
        (i) => {
          if (parseFloat(i.day_number) === parseFloat(day)) {
            return {
              ...i,
              ...data,
            };
          }
          return i;
        },
      );

      setDeliveryDates((state) => ({
        ...state,
        [deliveryActiveTab]: {
          ...state[deliveryActiveTab],
          opening_hours: newOpeningHours,
          errors: null,
        },
      }));

      try {
        await api.updateOpeningHours(deliveryActiveTab, {
          opening_hours: newOpeningHours,
          settings: currentTab?.settings,
        });

        const { data: serverData } = await api.get(
          `opening_hours/${deliveryActiveTab}`,
        );
        setDeliveryDates((state) => ({
          ...state,
          [deliveryActiveTab]: {
            ...state[deliveryActiveTab],
            isFetched: true,
            errors: null,
            ...serverData,
          },
        }));
        UIHelper.showSuccessMessage();
      } catch (e) {
        console.log(e);
        setHoursErrors(e?.response?.data?.opening_hours);
        UIHelper.showError(
          'Errore durante il salvataggio degli orari di apertura',
        );
      }
    },
    [api, deliveryActiveTab, deliveryDates],
  );

  const fetchDeliveryDates = useCallback(
    async (key) => {
      const { isFetched } = deliveryDates[key];
      if (isFetched) {
        return;
      }
      setLoadingStatuses((state) => ({
        ...state,
        [key]: true,
      }));
      try {
        const { data } = await api.get(`opening_hours/${key}`);
        setDeliveryDates((state) => ({
          ...state,
          [key]: {
            ...state[key],
            isFetched: true,
            errors: null,
            ...data,
          },
        }));
      } finally {
        setLoadingStatuses((state) => ({
          ...state,
          [key]: false,
        }));
      }
    },
    [api, deliveryDates],
  );

  const onChangeDeliveryTab = useCallback(
    (key) => {
      const currentData = deliveryDates[key];

      if (!currentData.isFetched) {
        setLoadingStatuses((s) => ({
          ...s,
          [key]: true,
        }));
      }

      setDeliveryActiveTab(key);
    },
    [loadingStatuses],
  );

  const handleChangeDeliveryZips = (index, data) => {
    setDeliveryZips((s) => {
      return s.map((i, ind) => {
        if (ind === index) {
          return data;
        }
        return i;
      });
    });
  };

  const onDeleteException = useCallback(
    async (data) => {
      try {
        const allExceptions =
          deliveryDates[deliveryActiveTab]?.opening_hour_exceptions;
        if (!allExceptions) {
          return;
        }
        const updateData = [];
        allExceptions.forEach((exception) => {
          const isRealId = Number.isInteger(data.id);
          if (isRealId && exception.id === data.id) {
            updateData.push({
              ...exception,
              _destroy: true,
            });
            // return;
          }
          // if (isRealId) {
          //   return updateData.push(exception);
          // }
          // const { id, ...restException } = exception;
          // updateData.push(restException);
        });
        await api.put(`opening_hours/${deliveryActiveTab}`, {
          data: {
            opening_hour_exceptions_attributes: updateData,
          },
        });
        try {
          const { data } = await api.get(`opening_hours/${deliveryActiveTab}`);
          setDeliveryDates((state) => ({
            ...state,
            [deliveryActiveTab]: {
              ...state[deliveryActiveTab],
              isFetched: true,
              errors: null,
              ...data,
            },
          }));
        } finally {
          setLoadingStatuses((state) => ({
            ...state,
            [deliveryActiveTab]: false,
          }));
        }
      } catch (e) {
        console.log(e);
      } finally {
        UIHelper.showSuccessMessage();
      }
    },
    [api, deliveryActiveTab, fetchDeliveryDates, deliveryDates],
  );

  return {
    deliveryZips,
    handleChangeDeliveryZips,
    deliveryDates,
    fetchDeliveryDates,
    loadingStatuses,
    changeDeliveryDayConfig,
    deliveryActiveTab,
    onChangeDeliveryTab,
    setHoursErrors,
    onChangeMaximum,
    onAddNewException,
    onChangeExceptionDay,
    onAddExceptionData,
    isLoading,
    onDeleteException,
    setDeliveryDates,
  };
};

export const useSettingsMainForm = (autoSaveConfigs = false) => {
  const [localState, setLocalState] = useState({});
  const [deliveryZips, setDeliveryZips] = useState([
    // {
    //   zone: 'first_zone',
    //   delivery_zip_attributes: {
    //     city: 'Gerlando ligure',
    //     cap: '69447',
    //   },
    // },
  ]);
  const [rooms, setRooms] = useState([]);
  const [dataForRequest, setDataForRequest] = useState({});

  const { currentRestaurant } = useCommon();

  const settingsApi = useMemo(() => {
    return new SettingsApi(currentRestaurant?.id);
  }, [currentRestaurant]);

  const {
    handleGetSettings,
    getInProcess,
    data,
    handleUpdate,
    updateInProcess,
    errors,
    resetErrors,
  } = useSettings();

  useEffect(() => {
    if (!getInProcess && Object.keys(data).length) {
      setLocalState(data);
      const preparedZips = [];
      data.delivery_zips_restaurants_attributes?.forEach((i) => {
        preparedZips.push({
          ...i,
          uuid: nanoid(),
        });
      });
      setDeliveryZips(preparedZips);
    }
  }, [data, getInProcess]);

  const onChange = useCallback(
    (e) => {
      const {
        target: { name, value },
      } = e;
      setLocalState((s) => {
        return {
          ...s,
          [name]: value,
        };
      });
      if (errors) {
        setErrors(null);
      }
    },
    [errors],
  );

  const onClickCreateDelivery = useCallback((name, zone) => {
    setDeliveryZips((s) => [
      ...s,
      { zone, uuid: nanoid(), delivery_zip_attributes: { city: '', cap: '' } },
    ]);
  }, []);

  const onDeleteDelivery = useCallback(
    async (i, index) => {
      setDeliveryZips((s) => {
        const updatedZips = [];
        s.forEach((zip) => {
          console.log('z ', zip);
          if (i.id ? zip.id === i.id : zip.uuid === i.uuid) {
            return zip.id && updatedZips.push({ ...zip, _destroy: true });
          }
          updatedZips.push(zip);
        });
        return updatedZips;
      });
      // try {
      //   setDeliveryZips((s) =>
      //     s.filter((d, ind) => (i.id ? d.id !== i.id : ind !== index)),
      //   );
      // } catch (e) {}
    },
    [settingsApi],
  );

  const handleChangeDeliveryZips = useCallback(
    (e, uuid) => {
      const {
        target: { name, value },
      } = e;
      const [realName] = name.split('_');
      setDeliveryZips((s) => {
        return s.map((i) => {
          if (i.uuid === (uuid || 1)) {
            return {
              ...i,
              delivery_zip_attributes: {
                ...i.delivery_zip_attributes,
                [realName]: value,
              },
            };
          }
          return i;
        });
      });
      resetErrors && resetErrors();
    },
    [resetErrors],
  );

  const onSubmit = useCallback(async () => {
    handleUpdate({
      settings: {
        ...dataForRequest,
        delivery_zips_restaurants_attributes: deliveryZips,
      },
    });
  }, [handleUpdate, dataForRequest, deliveryZips]);

  const onChangeRoom = useCallback(
    (name, value, item) => {
      setLocalState((s) => {
        const services_rooms_attributes = [];
        s.services_rooms_attributes.forEach((i) => {
          if (i.uuid === item.uuid) {
            const data = { ...i, [name]: value };
            services_rooms_attributes.push(data);
            return;
          }
          services_rooms_attributes.push(i);
        });
        return {
          ...s,
          services_rooms_attributes,
        };
      });
    },
    [rooms],
  );

  const onSelectConfig = useCallback(
    (item) => {
      setLocalState((state) => {
        const newState = { ...state };
        newState[item.id] = !localState[item.id];
        return newState;
      });
      setDataForRequest((state) => {
        const newState = { ...state };
        newState[item.id] = !localState[item.id];
        return newState;
      });
      if (autoSaveConfigs) {
        handleUpdate({
          settings: { ...dataForRequest, [item.id]: !localState[item.id] },
        });
      }
    },
    [localState, handleUpdate, dataForRequest, autoSaveConfigs],
  );

  return {
    onSubmit,
    onSelectConfig,
    handleChangeDeliveryZips,
    onDeleteDelivery,
    onClickCreateDelivery,
    onChange,
    updateInProcess,
    localState,
    deliveryZips,
    dataForRequest,
    handleGetSettings,
    errors,
    onChangeRoom,
    rooms,
  };
};

export const getInitialServiceData = () => ({
  // id: '',
  title: '',
  total_seats: 1,
  service_duration: '180_minutes', // 30_minutes, 60_minutes, 90_minutes, 120_minutes, 150_minutes, 180_minutes
  prereservation_time: 'none', // none, 15_minutes, 30_minutes, 60_minutes,
  confirmation_type: 'none', // none, until_hour, until_start, until_half,
  slot_duration: '30_minutes', // 15_minutes, 30_minutes
  slot_seats: 3,
  price: '1',
  booking_max_person_count: 6,
  auto_confirmation: true,
  services_rooms_attributes: [
    {
      room_id: '',
      total_seats: '',
      uuid: nanoid(),
    },
  ],
  service_opening_hours_attributes: [
    {
      day_number: 0,
      time_from: '10:00',
      time_to: '14:00',
      day_off: true,
    },
    {
      day_number: 1,
      time_from: '10:00',
      time_to: '14:00',
      day_off: true,
    },
    {
      day_number: 2,
      time_from: '10:00',
      time_to: '14:00',
      day_off: true,
    },
    {
      day_number: 3,
      time_from: '10:00',
      time_to: '14:00',
      day_off: true,
    },
    {
      day_number: 4,
      time_from: '10:00',
      time_to: '14:00',
      day_off: true,
    },
    {
      day_number: 5,
      time_from: '10:00',
      time_to: '14:00',
      day_off: true,
    },
    {
      day_number: 6,
      time_from: '10:00',
      time_to: '14:00',
      day_off: true,
    },
  ],
});

export const useServiceForm = (isEdit = false, initialData = null) => {
  const { currentRestaurant } = useCommon();
  const [rooms, setRooms] = useState([]);
  const [localState, setLocalState] = useState(
    initialData || getInitialServiceData(),
  );
  const [errors, setErrors] = useState(null);
  const key = 'service_opening_hours_attributes';

  const restaurantApi = new RestaurantsApi();

  useEffect(() => {
    if (currentRestaurant?.id) {
      (async () => {
        console.log('Loading rooms for restaurant:', currentRestaurant?.id);
        const rooms = await fetchAllPages({
          fetchPage: ({ page, per }) =>
            restaurantApi.get(`${currentRestaurant?.id}/rooms`, {
              params: {
                page,
                per,
              },
            }),
          getItems: (data) => data.rooms,
        });

        console.log('Rooms loaded:', rooms);
        setRooms(rooms);
      })();
    }
  }, [currentRestaurant]);

  const onClickChip = useCallback(
    (item) => {
      setLocalState((s) => ({
        ...s,
        [key]: s[key].map((i) => {
          const [day_number] = item.id.split('-');
          if (i.day_number === parseInt(day_number)) {
            return {
              ...i,
              day_off: !i.day_off,
            };
          }
          return i;
        }),
      }));
      if (errors) {
        setErrors(null);
      }
    },
    [errors, key],
  );

  const onChange = useCallback(
    (e) => {
      const {
        target: { name, value, type, checked },
      } = e;
      
      setLocalState((s) => {
        // Handle different input types
        const newValue = type === 'checkbox' ? checked : value;
        
        return {
          ...s,
          [name]: newValue,
        };
      });
      if (errors) {
        setErrors(null);
      }
    },
    [errors],
  );

  const onChangeRoom = useCallback(
    (name, value, item) => {
      setLocalState((s) => {
        const services_rooms_attributes = [];
        s.services_rooms_attributes.forEach((i) => {
          if (i.uuid === item.uuid) {
            const data = { ...i, [name]: value };
            services_rooms_attributes.push(data);
            return;
          }
          services_rooms_attributes.push(i);
        });
        return {
          ...s,
          services_rooms_attributes,
        };
      });
    },
    [rooms],
  );

  const onSelectTime = useCallback(
    (e, index) => {
      const {
        target: { name, value },
      } = e;
      const [itemName, day_number] = name.split('-');
      setLocalState((s) => ({
        ...s,
        [key]: s[key].map((i) => {
          if (i.day_number === parseInt(day_number)) {
            return {
              ...i,
              [itemName]: value,
            };
          }
          return i;
        }),
      }));
      if (errors) {
        setErrors(null);
      }
    },
    [errors, key],
  );

  const onChangeServiceRoom = useCallback(
    (e) => {
      const {
        target: { name, value },
      } = e;
      setLocalState((s) => {
        const newArr = [...s[name]];
        if (value === 'false') {
          // need add new item
          // TODO P.S. FUCK THIS CODE
          newArr.push({ uuid: nanoid(), title: '', total_seats: 1 });
        } else if (newArr.length) {
          newArr.pop();
        }
        return {
          ...s,
          [name]: newArr,
        };
      });
      if (errors) {
        setErrors(null);
      }
    },
    [errors],
  );

  const onChangeRoomsCount = useCallback((e) => {}, []);

  const onClickRoomsCount = useCallback(
    (type) => {
      if (type === 'increment') {
        setLocalState((s) => ({
          ...s,
          // TODO P.S. FUCK THIS CODE
          services_rooms_attributes: [
            ...s.services_rooms_attributes,
            { uuid: nanoid(), title: '', total_seats: 1 },
          ],
        }));
      }
      if (type === 'decrement') {
        setLocalState((s) => {
          const newArr = [...s.services_rooms_attributes];
          newArr.length > 0 && newArr.pop();
          return {
            ...s,
            services_rooms_attributes: newArr,
          };
        });
      }
      if (errors) {
        setErrors(null);
      }
    },
    [errors],
  );

  const onChangeRoomItem = useCallback(
    (value, name, room) => {
      setLocalState((s) => {
        return {
          ...s,
          services_rooms_attributes: s.services_rooms_attributes.map((i) => {
            if (room.id ? room.id === i.id : i.uuid === room.uuid) {
              return {
                ...i,
                [name]: value,
              };
            }
            return i;
          }),
        };
      });
      if (errors) {
        setErrors(null);
      }
    },
    [errors],
  );

  const onClickAddRoom = useCallback(() => {
    setLocalState((s) => {
      return {
        ...s,
        services_rooms_attributes: [
          ...(s.services_rooms_attributes || []),
          {
            room_id: '',
            total_seats: 1,
            uuid: nanoid(),
          },
        ],
      };
    });
  }, []);

  return {
    onSelectTime,
    onChange,
    onClickChip,
    localState,
    onChangeServiceRoom,
    onChangeRoomsCount,
    onClickRoomsCount,
    onChangeRoomItem,
    setErrors,
    errors,
    setLocalState,
    onChangeRoom,
    rooms,
    onClickAddRoom,
  };
};
